import React, { useState, useEffect } from 'react';
import { Star, TrendingUp, TrendingDown, X, AlertCircle, Building2, Plus, LayoutGrid, List, Activity, BarChart3 } from 'lucide-react';
import WatchlistButton from './WatchlistButton';
import CompanyLogo from './CompanyLogo';
import { useTranslation } from 'react-i18next';

export default function Watchlist({ user, userRole, guestWatchlist, onSelectStock, onSelectTheme }) {
  console.log('🎯 Watchlist component rendered with:', { user: !!user, guestWatchlist: guestWatchlist?.length });
  const { t } = useTranslation();
  const [watchlistCompanies, setWatchlistCompanies] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('watchlist'); // 'watchlist', 'djia', 'sp500', 'nasdaq', 'russ2k', 'all'
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Fancy color classes like themes
  const fancyColorClasses = [
    { gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-50', border: 'border-amber-200' },
    { gradient: 'from-cyan-500 to-blue-600', bg: 'bg-cyan-50', border: 'border-cyan-200' },
    { gradient: 'from-purple-500 to-indigo-600', bg: 'bg-purple-50', border: 'border-purple-200' },
    { gradient: 'from-green-500 to-emerald-600', bg: 'bg-green-50', border: 'border-green-200' },
    { gradient: 'from-orange-500 to-red-600', bg: 'bg-orange-50', border: 'border-orange-200' },
    { gradient: 'from-blue-500 to-cyan-600', bg: 'bg-blue-50', border: 'border-blue-200' },
    { gradient: 'from-pink-500 to-purple-600', bg: 'bg-pink-50', border: 'border-pink-200' },
    { gradient: 'from-teal-500 to-cyan-600', bg: 'bg-teal-50', border: 'border-teal-200' },
    { gradient: 'from-lime-500 to-green-600', bg: 'bg-lime-50', border: 'border-lime-200' },
    { gradient: 'from-purple-600 to-pink-600', bg: 'bg-purple-50', border: 'border-purple-200' },
    { gradient: 'from-indigo-500 to-blue-600', bg: 'bg-indigo-50', border: 'border-indigo-200' },
    { gradient: 'from-sky-500 to-blue-600', bg: 'bg-sky-50', border: 'border-sky-200' },
    { gradient: 'from-lime-600 to-green-600', bg: 'bg-lime-50', border: 'border-lime-200' },
    { gradient: 'from-rose-500 to-pink-600', bg: 'bg-rose-50', border: 'border-rose-200' }
  ];

  // Generate consistent color for each ticker
  const getTickerColorClasses = (ticker) => {
    if (!ticker) return fancyColorClasses[0];
    const hash = ticker.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return fancyColorClasses[hash % fancyColorClasses.length];
  };

  // Filter companies by index
  const getCompaniesByIndex = (index) => {
    if (!index || index === 'all') return allCompanies;
    return allCompanies.filter(company => company.index === index.toUpperCase());
  };

  // Get price data from prices state or embedded in company object
  const getPriceData = (company) => {
    // First check prices state (fresher data)
    if (prices[company.ticker]) {
      return prices[company.ticker];
    }
    // Fallback to embedded price data from company object
    if (company.price !== undefined) {
      return {
        price: company.price,
        change: company.change,
        changePercent: company.changePercent
      };
    }
    return null;
  };


  useEffect(() => {
    // Load companies and watchlist in parallel (don't wait for prices)
    loadAllCompanies();
    if (user) {
      loadWatchlist();
    } else if (!guestWatchlist || guestWatchlist.length === 0) {
      // Only set loading to false if there's no guest watchlist
      setLoading(false);
    }
    // If there's a guest watchlist, keep loading until loadGuestWatchlist is called
  }, [user, guestWatchlist]);

  // Separate useEffect for guest watchlist to ensure allCompanies is loaded
  useEffect(() => {
    console.log('Guest watchlist useEffect:', { 
      hasUser: !!user, 
      guestWatchlistLength: guestWatchlist?.length, 
      allCompaniesLength: allCompanies.length 
    });
    if (!user && guestWatchlist && guestWatchlist.length > 0 && allCompanies.length > 0) {
      console.log('Loading guest watchlist...');
      loadGuestWatchlist();
    }
  }, [guestWatchlist, allCompanies, user]);


  const loadAllCompanies = async () => {
    const cKey = 'watchlist_all_companies';
    const readCache = (k) => { try { const r = localStorage.getItem(k); if (!r) return null; const { ts, data } = JSON.parse(r); if (Date.now() - ts > 10 * 60 * 1000) { localStorage.removeItem(k); return null; } return data; } catch { return null; } };
    const writeCache = (k, d) => { try { localStorage.setItem(k, JSON.stringify({ ts: Date.now(), data: d })); } catch {} };

    const cached = readCache(cKey);
    if (cached) setAllCompanies(cached);

    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 12000);
      const response = await fetch(`${API_URL}/api/companies`, { signal: ctrl.signal });
      clearTimeout(t);
      if (!response.ok) return;
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) return;
      const data = await response.json();

      if (data.success) {
        writeCache(cKey, data.companies || []);
        setAllCompanies(data.companies || []);
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error loading all companies:', error);
    }
  };

  const loadWatchlist = async () => {
    const cKey = `watchlist_${user.uid}`;
    const CACHE_TTL = 10 * 60 * 1000;
    const readCache = (k) => { try { const r = localStorage.getItem(k); if (!r) return null; const { ts, data } = JSON.parse(r); if (Date.now() - ts > CACHE_TTL) { localStorage.removeItem(k); return null; } return data; } catch { return null; } };
    const writeCache = (k, d) => { try { localStorage.setItem(k, JSON.stringify({ ts: Date.now(), data: d })); } catch {} };

    // Show cached data instantly
    const cached = readCache(cKey);
    if (cached) {
      setWatchlistCompanies(cached);
      setLoading(false);
      loadPrices(cached.map(c => c.ticker));
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 12000);
      const response = await fetch(`${API_URL}/api/watchlist/${user.uid}/details`, { signal: ctrl.signal });
      clearTimeout(t);
      if (!response.ok) { if (!cached) setError('Server error, please try again later'); return; }
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) { if (!cached) setError('Server error, please try again later'); return; }
      const data = await response.json();

      if (data.success) {
        writeCache(cKey, data.companies || []);
        setWatchlistCompanies(data.companies || []);
        if (data.companies && data.companies.length > 0) {
          loadPrices(data.companies.map(c => c.ticker));
        }
      } else if (!cached) {
        setError(data.error || 'Failed to load watchlist');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        if (!cached) setError('Server is not responding. Please try again later.');
      } else {
        console.error('Error loading watchlist:', error);
        if (!cached) setError('Failed to connect to server');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPrices = async (tickers) => {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 8000);
      const response = await fetch(`${API_URL}/api/prices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers }),
        signal: ctrl.signal,
      });
      clearTimeout(t);
      if (!response.ok) return;
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) return;
      const data = await response.json();
      if (data.success) {
        setPrices(data.prices);
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error loading prices:', error);
    }
  };

  const loadGuestWatchlist = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('loadGuestWatchlist called', { guestWatchlist, allCompaniesCount: allCompanies.length });
      // Wait for allCompanies to be loaded first
      if (allCompanies.length === 0) {
        // Companies not loaded yet, will retry when allCompanies updates
        console.log('allCompanies not loaded yet');
        setLoading(false);
        return;
      }
      
      // Filter allCompanies to get only the guest watchlist tickers
      const guestCompanies = allCompanies.filter(company => 
        guestWatchlist.includes(company.ticker)
      );
      console.log('Filtered guest companies:', guestCompanies.length, guestCompanies.map(c => c.ticker));
      setWatchlistCompanies(guestCompanies);
      
      // Load prices for guest watchlist
      if (guestWatchlist.length > 0) {
        await loadPrices(guestWatchlist);
      }
    } catch (error) {
      console.error('Error loading guest watchlist:', error);
      setError('Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  };

  const removeFromWatchlist = async (ticker) => {
    try {
      const response = await fetch(`${API_URL}/api/watchlist/${user.uid}/remove/${ticker}`, {
        method: 'DELETE',
      });

      const data = await response.json();
      if (data.success) {
        // Reload watchlist
        await loadWatchlist();
      }
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      setError('Failed to remove from watchlist');
    }
  };

  if (!user && (!guestWatchlist || guestWatchlist.length === 0)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">Please log in to view your watchlist</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading watchlist...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <p className="text-red-600">{error}</p>
          <button
            onClick={loadWatchlist}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'watchlist', label: t('watchlist.myWatchlist'), icon: Star, count: watchlistCompanies.length, showOnlyForUser: true },
    { id: 'djia', label: 'DJIA', icon: TrendingUp, count: getCompaniesByIndex('djia').length },
    { id: 'sp500', label: 'S&P 500', icon: TrendingUp, count: getCompaniesByIndex('sp500').length },
    { id: 'nasdaq', label: 'NASDAQ', icon: TrendingUp, count: getCompaniesByIndex('nasdaq').length },
    { id: 'russ2k', label: 'RUSS 2K', icon: TrendingUp, count: getCompaniesByIndex('russ2k').length },
    { id: 'all', label: t('watchlist.publicCompanies'), icon: Building2, count: allCompanies.length },
  ];

  console.log('Rendering Watchlist:', { 
    user: !!user, 
    activeTab, 
    watchlistCompaniesLength: watchlistCompanies.length,
    guestWatchlistLength: guestWatchlist?.length,
    loading,
    error
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Tab Navigation - Enhanced Design */}
        <div className="mb-2">
          <div className="bg-white rounded-2xl shadow-lg p-2">
            <div className="flex items-center justify-between mb-2">
              <nav className="grid grid-cols-6 gap-2 flex-1">
                {tabs.map((tab) => {
                  if (tab.showOnlyForUser && !user && (!guestWatchlist || guestWatchlist.length === 0)) return null;
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  const showCount = tab.id === 'watchlist';

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        relative py-4 px-3 rounded-xl font-bold text-base transition-all duration-200
                        ${isActive
                          ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-inner translate-y-1'
                          : 'bg-gradient-to-br from-gray-50 to-gray-100 text-gray-700 shadow-md hover:shadow-lg hover:from-gray-100 hover:to-gray-200 active:translate-y-1 active:shadow-inner'
                        }
                        border-b-4 ${isActive ? 'border-blue-800' : 'border-gray-300'}
                      `}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Icon className={`w-5 h-5 flex-shrink-0 ${isActive && tab.id === 'watchlist' ? 'fill-white' : ''}`} />
                        <span className="text-sm leading-tight">{tab.label}</span>
                        {showCount && (
                          <span className={`
                            inline-block min-w-[1.5rem] py-0.5 px-1.5 rounded-full text-xs font-extrabold text-center
                            ${isActive
                              ? 'bg-white/25 text-white ring-1 ring-white/30'
                              : 'bg-blue-100 text-blue-700'
                            }
                          `}>
                            {tab.count}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex justify-end">
          <div className="bg-white rounded-xl shadow-md p-1 inline-flex gap-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                viewMode === 'grid'
                  ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              {t('watchlist.gridView')}
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                viewMode === 'table'
                  ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <List className="w-4 h-4" />
              {t('watchlist.tableView')}
            </button>
          </div>
        </div>

      {/* My Watchlist Tab Content */}
      {activeTab === 'watchlist' && (
        <div className="mt-2">

          {(!user && (!guestWatchlist || guestWatchlist.length === 0)) ? (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center border border-gray-100">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-4">
                <AlertCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Please log in</h3>
              <p className="text-gray-600 text-lg">
                Log in to create and manage your watchlist
              </p>
            </div>
          ) : watchlistCompanies.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center border border-gray-100">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-200 mb-4">
                <Star className="w-10 h-10 text-yellow-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Your watchlist is empty</h3>
              <p className="text-gray-600 text-lg">
                Start adding companies to track their performance and stay updated!
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            // Grid View
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
              {watchlistCompanies.map((company) => {
                const priceData = getPriceData(company);
                const isPositive = priceData?.changePercent >= 0;
                const colorClasses = getTickerColorClasses(company.ticker);

                return (
                  <div
                    key={company.ticker}
                    className={`group ${colorClasses.bg} rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 overflow-visible border-2 ${colorClasses.border} cursor-pointer relative`}
                    onClick={() => onSelectStock && onSelectStock(company.ticker)}
                  >

                    <div className="p-4">
                      {/* Star Icon, Company Logo & Name */}
                      <div className="flex items-center gap-2">
                        <WatchlistButton
                          ticker={company.ticker}
                          user={user}
                          isInWatchlist={true}
                          size="xs"
                          className="flex-shrink-0"
                        
                          userRole={userRole}
                        />
                        <CompanyLogo ticker={company.ticker} size="md" />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-gray-900 truncate leading-tight">{company.name}</h3>
                          <p className="text-xs text-gray-500">{company.ticker}</p>
                        </div>
                      </div>
                    </div>

                    {/* Hover Detail Popup */}
                    <div className="absolute left-0 top-full mt-2 w-80 bg-white rounded-lg shadow-2xl border-2 border-gray-200 p-4 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                      {/* Arrow pointing to the card */}
                      <div
                        className="absolute left-8 top-0 -translate-y-full"
                        style={{
                          width: 0,
                          height: 0,
                          borderLeft: '10px solid transparent',
                          borderRight: '10px solid transparent',
                          borderBottom: '10px solid white',
                          filter: 'drop-shadow(0 -2px 2px rgba(0,0,0,0.1))'
                        }}
                      />

                      <div className="space-y-3">
                        {/* Stock Header */}
                        <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                          <CompanyLogo ticker={company.ticker} size="md" />
                          <div className="flex-1">
                            <h4 className="text-base font-bold text-gray-900">{company.name}</h4>
                            <p className="text-xs text-gray-500">{company.ticker}</p>
                          </div>
                        </div>

                        {/* Price Details */}
                        {priceData && (
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Current Price</p>
                                <p className="text-lg font-bold text-gray-900">${priceData.price?.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Change</p>
                                <p className={`text-lg font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                  {isPositive ? '+' : ''}{priceData.changePercent?.toFixed(2)}%
                                </p>
                              </div>
                            </div>
                            {priceData.volume && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className="text-xs text-gray-500 mb-1">Volume</p>
                                <p className="text-sm font-semibold text-gray-900">{priceData.volume.toLocaleString()}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Sector & Industry */}
                        {(company.sector || company.industry) && (
                          <div className="bg-gray-50 rounded-lg p-3">
                            {company.sector && (
                              <div className="mb-2">
                                <p className="text-xs text-gray-500 mb-1">Sector</p>
                                <p className="text-sm font-semibold text-gray-900">{company.sector}</p>
                              </div>
                            )}
                            {company.industry && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Industry</p>
                                <p className="text-sm font-semibold text-gray-900">{company.industry}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Index */}
                        {company.index && (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded">
                              {company.index}
                            </span>
                          </div>
                        )}

                        {/* Click hint */}
                        <div className="text-center text-xs text-gray-500 pt-2 border-t border-gray-200">
                          Click card to view full details
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Table View
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Ticker
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Change
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Sector
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {watchlistCompanies.map((company, index) => {
                      const priceData = getPriceData(company);
                      const isPositive = priceData?.changePercent >= 0;

                      return (
                        <tr
                          key={company.ticker}
                          className={`hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent cursor-pointer transition-all duration-200 border-b border-gray-50 last:border-0 ${
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-100'
                          }`}
                          onClick={() => onSelectStock && onSelectStock(company.ticker)}
                        >
                          <td className="px-6 py-1 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <CompanyLogo ticker={company.ticker} size="md" />
                              <div className="flex-1">
                                <div className="text-sm font-semibold text-gray-900">{company.name}</div>
                                <div className="text-xs text-gray-500 truncate max-w-xs mt-1">
                                  {company.description}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-1 whitespace-nowrap">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-700">
                              {company.ticker}
                            </span>
                          </td>
                          <td className="px-6 py-1 whitespace-nowrap">
                            {priceData ? (
                              <span className="text-base font-bold text-gray-900">
                                ${priceData.price?.toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">Loading...</span>
                            )}
                          </td>
                          <td className="px-6 py-1 whitespace-nowrap">
                            {priceData && (
                              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold ${isPositive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                {isPositive ? (
                                  <TrendingUp className="w-4 h-4" />
                                ) : (
                                  <TrendingDown className="w-4 h-4" />
                                )}
                                <span className="text-sm">
                                  {isPositive ? '+' : ''}{priceData.changePercent?.toFixed(2)}%
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-5 whitespace-nowrap">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                              {company.sector || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-5 whitespace-nowrap text-right">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFromWatchlist(company.ticker);
                              }}
                              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-all duration-200 hover:shadow-md"
                            >
                              <X className="w-4 h-4" />
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Index/Company Tabs Content (DJIA, S&P 500, NASDAQ, RUSS 2K, Public Companies) */}
      {activeTab !== 'watchlist' && (() => {
        const displayCompanies = activeTab === 'all' ? allCompanies : getCompaniesByIndex(activeTab);
        const tabName = tabs.find(t => t.id === activeTab)?.label || 'Companies';

        return (
          <div className="mt-2">
            {displayCompanies.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center border border-gray-100">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 mb-4">
                  <Building2 className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">No companies available</h3>
                <p className="text-gray-600 text-lg">
                  {activeTab === 'all'
                    ? 'Companies will appear here once they are added to the database.'
                    : `No companies found for ${tabName}.`}
                </p>
              </div>
            ) : viewMode === 'grid' ? (
              // Grid View for Index Tabs
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
                {displayCompanies.map((company) => {
                  const priceData = getPriceData(company);
                  const isPositive = priceData?.changePercent >= 0;
                  const colorClasses = getTickerColorClasses(company.ticker);

                  return (
                    <div
                      key={company.ticker}
                      className={`group ${colorClasses.bg} rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 overflow-visible border-2 ${colorClasses.border} cursor-pointer relative`}
                      onClick={() => onSelectStock && onSelectStock(company.ticker)}
                    >

                      <div className="p-4">
                        {/* Star Icon, Company Logo & Name */}
                        <div className="flex items-center gap-2">
                          <WatchlistButton
                            ticker={company.ticker}
                            user={user}
                            size="xs"
                            className="flex-shrink-0"
                          
                          userRole={userRole}
                        />
                          <CompanyLogo ticker={company.ticker} size="md" />
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-bold text-gray-900 truncate leading-tight">{company.name}</h3>
                            <p className="text-xs text-gray-500">{company.ticker}</p>
                          </div>
                        </div>
                      </div>

                      {/* Hover Detail Popup */}
                      <div className="absolute left-0 top-full mt-2 w-80 bg-white rounded-lg shadow-2xl border-2 border-gray-200 p-4 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                        {/* Arrow pointing to the card */}
                        <div
                          className="absolute left-8 top-0 -translate-y-full"
                          style={{
                            width: 0,
                            height: 0,
                            borderLeft: '10px solid transparent',
                            borderRight: '10px solid transparent',
                            borderBottom: '10px solid white',
                            filter: 'drop-shadow(0 -2px 2px rgba(0,0,0,0.1))'
                          }}
                        />

                        <div className="space-y-3">
                          {/* Stock Header */}
                          <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                            <CompanyLogo ticker={company.ticker} size="md" />
                            <div className="flex-1">
                              <h4 className="text-base font-bold text-gray-900">{company.name}</h4>
                              <p className="text-xs text-gray-500">{company.ticker}</p>
                            </div>
                          </div>

                          {/* Price Details */}
                          {priceData && (
                            <div className="bg-gray-50 rounded-lg p-3">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Current Price</p>
                                  <p className="text-lg font-bold text-gray-900">${priceData.price?.toFixed(2)}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Change</p>
                                  <p className={`text-lg font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                    {isPositive ? '+' : ''}{priceData.changePercent?.toFixed(2)}%
                                  </p>
                                </div>
                              </div>
                              {priceData.volume && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <p className="text-xs text-gray-500 mb-1">Volume</p>
                                  <p className="text-sm font-semibold text-gray-900">{priceData.volume.toLocaleString()}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Sector & Industry */}
                          {(company.sector || company.industry) && (
                            <div className="bg-gray-50 rounded-lg p-3">
                              {company.sector && (
                                <div className="mb-2">
                                  <p className="text-xs text-gray-500 mb-1">Sector</p>
                                  <p className="text-sm font-semibold text-gray-900">{company.sector}</p>
                                </div>
                              )}
                              {company.industry && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Industry</p>
                                  <p className="text-sm font-semibold text-gray-900">{company.industry}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Index */}
                          {company.index && (
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded">
                                {company.index}
                              </span>
                            </div>
                          )}

                          {/* Click hint */}
                          <div className="text-center text-xs text-gray-500 pt-2 border-t border-gray-200">
                            Click card to view full details
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Table View for Index Tabs
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Company
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Ticker
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Price
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Change
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Sector
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {displayCompanies.map((company, index) => {
                        const priceData = getPriceData(company);
                        const isPositive = priceData?.changePercent >= 0;

                        return (
                          <tr
                            key={company.ticker}
                            className={`hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent cursor-pointer transition-all duration-200 border-b border-gray-50 last:border-0 ${
                              index % 2 === 0 ? 'bg-white' : 'bg-gray-100'
                            }`}
                            onClick={() => onSelectStock && onSelectStock(company.ticker)}
                          >
                            <td className="px-6 py-1 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <CompanyLogo ticker={company.ticker} size="md" />
                                <div className="flex-1">
                                  <div className="text-sm font-semibold text-gray-900">{company.name}</div>
                                  <div className="text-xs text-gray-500 truncate max-w-xs mt-1">
                                    {company.description}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-1 whitespace-nowrap">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-700">
                                {company.ticker}
                              </span>
                            </td>
                            <td className="px-6 py-1 whitespace-nowrap">
                              {priceData ? (
                                <span className="text-base font-bold text-gray-900">
                                  ${priceData.price?.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-400">Loading...</span>
                              )}
                            </td>
                            <td className="px-6 py-1 whitespace-nowrap">
                              {priceData && (
                                <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold ${isPositive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                  {isPositive ? (
                                    <TrendingUp className="w-4 h-4" />
                                  ) : (
                                    <TrendingDown className="w-4 h-4" />
                                  )}
                                  <span className="text-sm">
                                    {isPositive ? '+' : ''}{priceData.changePercent?.toFixed(2)}%
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {company.sector || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                              <WatchlistButton
                                user={user}
                                ticker={company.ticker}
                                onWatchlistChange={loadWatchlist}
                                size="md"
                              
                          userRole={userRole}
                        />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })()}
      </div>
    </div>
  );
}
